import type { CarProfile, EngineLoopSample, OneShotSample } from '../cars/CarProfile';
import { candidateNames } from './sampleFiles';
import type { LoadedLoop } from './RPMMixer';
import type { LoadedSweep } from './SweepVoice';
import { synthesizeEngineLoop, synthesizeShift, synthesizeBackfire } from './ProceduralVoice';

/**
 * EngineSampleManager — loads a car's samples, falling back to procedural
 * audio for anything missing or undecodable. Never throws.
 */

export interface LoadReport {
  /** the car HAS recordings and not one could be fetched (entitlement refused, offline, 404) */
  denied?: boolean;
  /** Container actually used for the real samples ('ogg' | 'flac' | 'wav' | null when none decoded). */
  format: string | null;
  loopsReal: number;
  loopsProcedural: number;
  oneShotsReal: number;
  oneShotsProcedural: number;
  missing: string[];
}

export interface OneShots {
  limiter: AudioBuffer | null;
  launch: AudioBuffer | null;
  launchCabin: AudioBuffer | null;
  launchExtra: AudioBuffer | null;
  limiterCabin: AudioBuffer | null;
  limiterExtra: AudioBuffer | null;
  tipIn: LoadedLoop[];
  liftOff: LoadedLoop[];
  tipInCabin: LoadedLoop[];
  liftOffCabin: LoadedLoop[];
  tipInExtra: LoadedLoop[];
  liftOffExtra: LoadedLoop[];
  launchRpm: number;
  wind: AudioBuffer | null;
  debris: AudioBuffer | null;
  pullaway: AudioBuffer | null;
  settle: AudioBuffer | null;
  idleBed: AudioBuffer | null;
  overlayOn: LoadedLoop[];
  overlayOff: LoadedLoop[];
  simOn: LoadedLoop[];
  simOff: LoadedLoop[];
  extraOn: LoadedLoop[];
  extraOff: LoadedLoop[];
  extraMid: LoadedLoop[];
  midLoops: LoadedLoop[];
  overlayMid: LoadedLoop[];
  turbo: AudioBuffer | null;
  starter: AudioBuffer | null;
  startup: AudioBuffer | null;
  shutoff: AudioBuffer | null;
  falsestart: AudioBuffer | null;
  falsestarts: AudioBuffer[];
  ambience: AudioBuffer | null;
  pitLife: AudioBuffer[];
  /** showroom: the music bed and the storyteller, for a road car with no pit lane around it */
  music: AudioBuffer | null;
  story: AudioBuffer | null;
  doorOpen: AudioBuffer | null;
  doorShut: AudioBuffer | null;
  /** rpm at which each shiftUp / shiftDown clip was recorded (0 = unknown) */
  shiftUpRpm: number[];
  shiftDownRpm: number[];
  /** rpm the limiter bed was recorded at (0 = unknown) */
  limiterRpm: number;
  lowLoops: { buffer: AudioBuffer; rpm: number }[];
  shiftUp: AudioBuffer[];
  shiftDown: AudioBuffer[];
  backfire: AudioBuffer[];
}

/** wind, stones, garage tone and the cars going past: the atmosphere, not the engine */
export interface EnvironmentBuffers {
  wind: AudioBuffer | null; debris: AudioBuffer | null; ambience: AudioBuffer | null; pitLife: AudioBuffer[];
  /** the road cars' showroom: a music bed and a voice talking about the car, both parked-only */
  music: AudioBuffer | null; story: AudioBuffer | null;
}

export interface LoadedCarAudio {
  on: LoadedLoop[];
  off: LoadedLoop[];
  /** granular sweep material (samples.sweeps) — when present the engine voice is the SweepVoice */
  sweeps: LoadedSweep[];
  oneShots: OneShots;
  report: LoadReport;
}

const yieldToUI = () => new Promise<void>((r) => setTimeout(r, 0));

/**
 * Sample files are referenced WITHOUT extension in the profile; we pick the
 * best container this browser can decode. Opus (tiny, gapless) for Chromium /
 * Tesla, FLAC (lossless) for Safari and anything else, WAV as last resort.
 */
// FLAC FIRST, EVERYWHERE. Safari never took the Opus files and Safari never had the horn; Chrome took
// them and did, on the same Mac, the same speakers, the same drive (Marc, and it is the cleanest
// signal in two days of hunting). The decoded buffers measure the same, so this is not a decoder bug
// we can point at, but the lossless file is the one that has never misbehaved in any browser and the
// difference in the ear is what decides. Opus stays on disk and is still served on request; it just
// stops being the default.
const FORMATS: { ext: string; mime: string }[] = [
  { ext: 'flac', mime: 'audio/flac' },
  { ext: 'ogg', mime: 'audio/ogg; codecs="opus"' },
  { ext: 'wav', mime: 'audio/wav' },
];

export function preferredFormats(): string[] {
  let probe: HTMLAudioElement | null = null;
  try {
    probe = typeof document !== 'undefined' ? document.createElement('audio') : null;
  } catch {
    probe = null;
  }
  const supported = FORMATS.filter((f) => !probe || probe.canPlayType(f.mime) !== '').map((f) => f.ext);
  const rest = FORMATS.map((f) => f.ext).filter((e) => !supported.includes(e));
  return [...supported, ...rest]; // still try the others if the probe lied
}

function hasExtension(file: string): boolean {
  return /\.[a-z0-9]{2,4}$/i.test(file);
}

/** DEMO audio arrives AES-256-CTR encrypted (zero IV) with the grant's per-token key; decrypt in memory, never store. */
async function decryptDemo(data: ArrayBuffer, keyHex: string): Promise<ArrayBuffer> {
  const raw = new Uint8Array(keyHex.match(/.{2}/g)!.map((h) => parseInt(h, 16)));
  const key = await crypto.subtle.importKey('raw', raw, { name: 'AES-CTR' }, false, ['decrypt']);
  return crypto.subtle.decrypt({ name: 'AES-CTR', counter: new Uint8Array(16), length: 128 }, key, data);
}

/** what the loading bar hears from one download: bytes as they arrive, then the moment the CPU takes over */
interface FetchHooks {
  onBytes?: (got: number, len: number) => void;
  onDecode?: () => void;
}

/** the body, read chunk by chunk when someone wants to watch it arrive (the plain arrayBuffer() otherwise) */
async function readBody(res: Response, onBytes?: (got: number, len: number) => void): Promise<ArrayBuffer> {
  const len = Number(res.headers.get('content-length')) || 0;
  if (!onBytes || !res.body || typeof res.body.getReader !== 'function') return res.arrayBuffer();
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let got = 0;
  onBytes(0, len);
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) { chunks.push(value); got += value.byteLength; onBytes(got, len); }
  }
  const out = new Uint8Array(got);
  let o = 0;
  for (const c of chunks) { out.set(c, o); o += c.byteLength; }
  return out.buffer;
}

async function fetchDecode(ctx: BaseAudioContext, url: string, demoKey = '', hooks?: FetchHooks): Promise<AudioBuffer | null> {
  try {
    const res = await fetch(demoKey ? `${url}${url.includes('?') ? '&' : '?'}d=1` : url, { cache: demoKey ? 'no-store' : 'force-cache' });
    if (!res.ok) return null;
    const type = res.headers.get('content-type') ?? '';
    if (type.includes('text/html')) return null; // SPA fallback page, not audio
    let data = await readBody(res, hooks?.onBytes);
    hooks?.onDecode?.();
    if (demoKey && res.headers.get('x-roar-enc') === 'aes-256-ctr') data = await decryptDemo(data, demoKey);
    if (data.byteLength < 64) return null;
    return await ctx.decodeAudioData(data);
  } catch {
    return null;
  }
}

/**
 * THE SEAM'S LAST FRAME IS THE FIRST FRAME. A rung is a region inside one long bounce, and the regions
 * abut: the frame at `loopEnd` is the next rung's first sample. A player interpolating between the
 * region's last frame and 'the next one' can reach across that boundary at every wrap, which is one
 * sample of the wrong rung a few times a second (Marc, in the Tesla: 'todo lo que acaba y vuelve a
 * empezar suena ese click'). The region's last frame is overwritten with its own first one and the
 * loop ends a frame early, so whichever frame a browser reads past the end, it reads the loop's start.
 * One sample in two seconds; nothing else about the take moves. The bounce itself is untouched.
 */
/**
 * THE CLICKS ARE IN THE FILE. Each rung of a bounce was rendered by streaming the bank's own loop
 * round and round (tools/single-file.py loop_stream), and every time that source loop came back to
 * its start it did so on a hard cut, which the render kept: the Aventador's idle rung carries a
 * click every 4.18 and 4.88 s, the 458's every 2.27 s, the Porsche's bounce none at all. Marc heard
 * exactly this in the Tesla ('todo lo que acaba y vuelve a empezar suena ese click'), and its
 * speakers are the first that did not hide it. The sources the bounces were made from are no longer
 * in the tree, so the repair is done here, once per decoded buffer: a kink whose second difference
 * stands twelve times above its neighbourhood's mean is blended, over 2.7 ms, into a straight line
 * under a Hann window, which leaves both edges continuous in slope. Measured on the four bounces:
 * 61 / 142 / 57 / 14 spots patched, none left on a second pass.
 */
const declicked = new WeakSet<AudioBuffer>();
/** where declickBuffer patched: every hard cut it found, so fitLoop can loop AROUND them */
const patchedAt = new WeakMap<AudioBuffer, number[]>();
/**
 * `edges` are the region boundaries in seconds (every rung's loopStart and loopEnd). Two abutting
 * regions are two rpm butted together, which is the biggest kink in the whole file, and v497's
 * first version patched exactly those: the blend rewrote the last 64 samples of one rung and the
 * first 64 of the next, so every wrap of every voice landed on a rewritten tail and head and
 * stepped (measured on the mixer bus: a 0.1 step at each wrap, ~7 in 18 s of idle). Nothing within
 * 80 frames of an edge is touched now.
 */
export function declickBuffer(buffer: AudioBuffer, edges: number[] = []): void {
  if (declicked.has(buffer)) return;
  declicked.add(buffer);
  const cuts: number[] = [];
  patchedAt.set(buffer, cuts);
  const THR = 12, HALF = 64, BLK = 2048, KEEP = 80;
  const edgeFrames = edges.map((t) => Math.round(t * buffer.sampleRate)).sort((p, q) => p - q);
  const nearEdge = (k: number): boolean => {
    // binary search for the nearest edge
    let lo = 0, hi = edgeFrames.length - 1;
    while (lo <= hi) {
      const m = (lo + hi) >> 1;
      if (Math.abs(edgeFrames[m] - k) <= KEEP) return true;
      if (edgeFrames[m] < k) lo = m + 1; else hi = m - 1;
    }
    return false;
  };
  const w = new Float32Array(2 * HALF + 1);
  for (let i = 0; i < w.length; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (w.length - 1));
  const d2 = new Float32Array(BLK);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const y = buffer.getChannelData(c);
    const n = y.length - 2;
    if (n <= 0) continue;
    // block by block: the second difference of one block, its mean as the local scale, then the scan.
    // A patch may reach into the next block; the samples it rewrites are already behind the scan.
    for (let b0 = 0; b0 < n; b0 += BLK) {
      const len = Math.min(BLK, n - b0);
      let acc = 0;
      for (let j = 0; j < len; j++) { const i = b0 + j; const v = Math.abs(y[i + 2] - 2 * y[i + 1] + y[i]); d2[j] = v; acc += v; }
      const thr = THR * (acc / len + 1e-6);
      let j = 0;
      while (j < len) {
        if (d2[j] > thr) {
          const k = b0 + j + 1, a = k - HALF, b = k + HALF;
          if (nearEdge(k)) { j += KEEP + 1; continue; }
          if (c === 0) cuts.push(k);
          if (a >= 0 && b < y.length) {
            const ya = y[a], yb = y[b];
            for (let q = 0; q <= 2 * HALF; q++) {
              const lin = ya + ((yb - ya) * q) / (2 * HALF);
              y[a + q] = y[a + q] * (1 - w[q]) + lin * w[q];
            }
          }
          j += HALF + 1;
        } else j++;
      }
    }
  }
}

/** every loopStart / loopEnd the profile declares, across all its ladders: the region edges of its bounce */
function ladderEdges(profile: CarProfile): number[] {
  const s = profile.samples;
  const lists: (EngineLoopSample[] | undefined)[] = [s.on, s.off, s.mid, s.overlay?.on, s.overlay?.off, s.overlay?.mid, s.sim?.on, s.sim?.off, s.extra?.on, s.extra?.off, s.extra?.mid];
  const out: number[] = [];
  for (const l of lists) for (const x of l ?? []) { if (x.loopStart !== undefined) out.push(x.loopStart); if (x.loopEnd !== undefined) out.push(x.loopEnd); }
  return out;
}

/**
 * THE IDLE HAS PARONES, and they are in the file too.
 *
 * Marc: 'el idle del Aventador tiene parones, no está bien construido el loop, se nota que no es un
 * sonido constante'. Measured on his bank: the 12 s idle region carries FOUR hard cuts at 4.180,
 * 4.878, 8.360 and 9.756 s, where tools/single-file.py butted one pass of the source loop onto the
 * next. The declicker above takes the CLICK off them, but a splice is not a click: the firing phase
 * of a V12 jumps, and 2.7 ms of straight line in an 11 ms cycle is a hole. Four of those, unevenly
 * spaced, is exactly a sound that is not constant. And the wrap itself was landing wherever the
 * region happened to end: measured across the Aventador's sixty rungs, HALF of them looped back onto
 * an inverted waveform (on_3850 scored -0.71, off_3050 -0.95), which is a stumble every two seconds
 * in every gear, not only at idle.
 *
 * So the loop is FITTED to the material instead of taken from the region:
 *  1. the longest stretch of the region with no hard cut in it (the idle keeps 4.18 s of its 12, the
 *     two-second rungs have none and keep all of it); anything under 1.5 s is not worth the
 *     repetition and the whole region stands.
 *  2. the end walked back up to one firing period — known exactly from the rung's own rpm and the
 *     engine's cylinder count — onto the offset whose waveform best matches the loop's start.
 * Measured on the Aventador's idle: the seam goes from 0.78 to 0.945 correlated AND stops crossing
 * four splices; on_3850 goes from -0.71 to 0.72, off_2725 from 0.41 to 0.98.
 */
const fitted = new WeakMap<AudioBuffer, Map<string, { loopStart: number; loopEnd: number }>>();
/**
 * The hard cuts inside a region. They come from the declicker rather than from a fresh scan, because
 * by the time a loop is fitted the declicker has already run and smoothed the very kinks we are
 * looking for: scanning the repaired samples finds nothing, which is how the first version of this
 * silently did nothing at all to the Aventador's idle. Kinks the declicker skipped as region edges
 * are skipped here too - a region boundary is not a cut inside the region.
 */
const SPLICE_PAD_S = 0.06;
function spliceFrames(buffer: AudioBuffer, a: number, b: number): number[] {
  const pad = Math.round(SPLICE_PAD_S * buffer.sampleRate);
  return (patchedAt.get(buffer) ?? []).filter((i) => i - a > pad && b - i > pad);
}
export function fitLoop(buffer: AudioBuffer, loopStart: number | undefined, loopEnd: number | undefined, recordedRpm: number, cylinders: number): { loopStart?: number; loopEnd?: number } {
  if (loopEnd === undefined) return { loopStart, loopEnd };
  const sr = buffer.sampleRate;
  const a0 = Math.max(0, Math.round((loopStart ?? 0) * sr));
  const b0 = Math.min(buffer.length, Math.round(loopEnd * sr));
  if (b0 - a0 < sr * 0.3) return { loopStart, loopEnd };
  const key = `${a0}:${b0}`;
  let perBuffer = fitted.get(buffer);
  if (!perBuffer) { perBuffer = new Map(); fitted.set(buffer, perBuffer); }
  const seen = perBuffer.get(key);
  if (seen) return seen;
  const d = buffer.getChannelData(0);
  // 1) the longest stretch of the region that no hard cut runs through. The 64-sample margin keeps the
  // declicker's own patch out of the loop: the loop starts after the repair and ends before the next.
  const cuts = spliceFrames(buffer, a0, b0);
  const marks = [a0 - 64, ...cuts, b0 + 64];
  let a = a0, b = b0, widest = 0;
  for (let i = 0; i + 1 < marks.length; i++) {
    const s0 = marks[i] + 64, e0 = marks[i + 1] - 64;
    if (e0 - s0 > widest) { widest = e0 - s0; a = s0; b = e0; }
  }
  // …but only if what is left is still a LOOP. Marc's other law is that a short idle repeats audibly
  // ('el idle se nota que cada 2 segundos se repite'), so a clean stretch is taken when it is either
  // three seconds long or nearly the whole region anyway. The Aventador's idle keeps 4.18 of its 12
  // and loses all four cuts; the 458's is chopped into six pieces of which the longest is 2.57 s, so
  // that one keeps its region and only the seam is fitted - a bank that can only be truly repaired by
  // re-rendering it from sources that are no longer in the tree.
  if (!(widest >= 3 * sr || widest >= 0.75 * (b0 - a0))) { a = a0; b = b0; }
  // 2) the seam, walked back onto the best match with the loop's own start
  const period = recordedRpm > 0 && cylinders > 0 ? Math.round((120 * sr) / (recordedRpm * cylinders)) : Math.round(0.011 * sr);
  const n = Math.min(Math.max(3 * period, Math.round(0.02 * sr)), 3000);
  const back = Math.min(period, b - a - n - 2);
  const untouched = a === a0 && b === b0;
  if (back > 1) {
    let sa = 0;
    for (let i = 0; i < n; i++) sa += d[a + i] * d[a + i];
    sa = Math.sqrt(sa) + 1e-12;
    let bestScore = -2, bestEnd = b, asIs = -2;
    for (let s = 0; s < back; s++) {
      const e = b - s;
      let dot = 0, sb = 0;
      for (let i = 0; i < n; i++) { const v = d[e - n + i]; dot += v * d[a + i]; sb += v * v; }
      const score = dot / (Math.sqrt(sb) * sa + 1e-12);
      if (s === 0) asIs = score;
      if (score > bestScore) { bestScore = score; bestEnd = e; }
    }
    // A region's own end is where the RENDER put its seam, crossfaded and all (v512), and moving off
    // it is only worth doing when the match is clearly better. v511 moved every end that scored a
    // hair higher and cut there dry, which put a click on every wrap of the Aventador's idle - Marc:
    // 'ahora escucho los clicks entre audio y audio'. A loop that already had to leave the region's
    // edges to dodge a cut has nothing to lose and takes the best offset outright.
    if (!untouched || bestScore > asIs + 0.12) b = bestEnd;
  }
  // 3) A MOVED EDGE IS A FRESH CUT, AND A CUT IS A CLICK, however well its phase matches: the two
  // samples either side still step. So wherever this loop no longer wraps on the render's own seam,
  // the head is crossfaded, in the buffer, from the material that really follows the new end - one
  // firing period of it, or whatever of the region is left past the end. The same repair the
  // renderer makes, made here for every file the car may still have cached.
  if (a !== a0 || b !== b0) {
    const xf = Math.min(Math.max(period, Math.round(0.005 * sr)), Math.round(0.03 * sr), b0 - b, b - a - 8);
    if (xf >= 8) {
      for (let c = 0; c < buffer.numberOfChannels; c++) {
        const y = buffer.getChannelData(c);
        for (let i = 0; i < xf; i++) {
          const t = (i / (xf - 1)) * Math.PI * 0.5;
          y[a + i] = y[b + i] * Math.cos(t) + y[a + i] * Math.sin(t);
        }
      }
    }
  }
  const fit = { loopStart: a / sr, loopEnd: b / sr };
  perBuffer.set(key, fit);
  return fit;
}

export async function loadCarAudio(
  ctx: BaseAudioContext,
  profile: CarProfile,
  onProgress?: (done: number, total: number) => void,
  /** signed entitlement token appended as ?g=… (the server refuses audio without it) */
  grantToken = '',
  /** DEMO grant: per-token key — the server sends the audio encrypted, we decrypt in memory */
  demoKey = '',
  /** hand the pit-box atmosphere over when it lands, instead of making the engine wait for it */
  onEnvironment?: (e: EnvironmentBuffers) => void,
): Promise<LoadedCarAudio> {
  // THE AUDIO VERSION RIDES THE URL. The server marks /audio immutable for a year and this loader asks
  // with force-cache, so once a file is in the browser's HTTP cache it stays there for as long as the
  // grant token (7 days) keeps the URL identical, whatever the service worker's cache version says:
  // Marc kept hearing the old redline rungs after two deploys that had replaced them on disk. The build
  // stamp (= the SW's audio version) in the query makes a changed bank a new URL for every browser.
  const rev = typeof __ROAR_BUILD__ !== 'undefined' ? __ROAR_BUILD__ : 'dev';
  const q = (grantToken ? `?g=${encodeURIComponent(grantToken)}&` : '?') + `r=${encodeURIComponent(rev)}`;
  const s = profile.samples;
  const report: LoadReport = { format: null, loopsReal: 0, loopsProcedural: 0, oneShotsReal: 0, oneShotsProcedural: 0, missing: [] };
  const base = s.basePath.replace(/\/$/, '');
  // THE ENGINE'S VOICE IS LOSSLESS, THE REST IS NOT. The seven rpm rungs and the sweeps are what you
  // hear every second of a drive, and they weigh 4 MB in FLAC. The sixty one-shots — doors, wrenches,
  // the pit row, the ambience under a parked car — are 51 MB in FLAC and 7 MB in Opus, and measured band
  // by band the two are the same sound within half a decibel. So a first drive costs 11 MB instead of
  // 55 over the car's LTE, and nothing that carries the engine was touched.
  const formats = preferredFormats();
  const chosen: Record<'loop' | 'shot', string | null> = { loop: null, shot: null };
  // ONE FETCH AND ONE DECODE PER FILE. A SINGLE FILE car points forty-odd rungs at the same
  // sprite - each with its own loop region - and without this that is forty downloads of the same
  // five megabytes and forty decodes of it. Keyed on the file, shared as a promise, so the rungs
  // that ask while the first is still in flight wait for it instead of starting their own.
  const inFlight = new Map<string, Promise<AudioBuffer | null>>();
  // THE BAR FOLLOWS THE BYTES. It used to count FILES, and a single-file car's whole voice is ONE
  // file of 9-10 MB that every rung waits for: the bar sat at 0 % for the whole download over the
  // car's LTE and then ran to 100 % in a blink (Marc, V12 in the Tesla). Now the engine's own file
  // is 75 % of the bar by its bytes, its decrypt + decode the next 10 % (crawling while the CPU
  // works, so a slow car does not look frozen), and the rungs and one-shots the last 15 %.
  let done = 0;
  let total = 0;
  const bank = new Map<string, { got: number; len: number }>();
  let decodeFrac = 0;
  let bankDone = false;
  let shown = 0;
  let creep: ReturnType<typeof setInterval> | null = null;
  const stopCreep = () => { if (creep) { clearInterval(creep); creep = null; } };
  const emit = () => {
    let got = 0;
    let len = 0;
    for (const b of bank.values()) { if (b.len > 0) { got += Math.min(b.got, b.len); len += b.len; } }
    const dl = bankDone ? 1 : len > 0 ? got / len : 0;
    const f = 0.75 * dl + 0.1 * (bankDone ? 1 : decodeFrac) + 0.15 * (total > 0 ? done / total : 0);
    shown = Math.max(shown, Math.min(1, f));
    onProgress?.(Math.round(shown * 1000), 1000);
  };
  const bankHooks = (file: string): FetchHooks => ({
    onBytes: (got, len) => { bank.set(file, { got, len }); emit(); },
    onDecode: () => {
      if (creep || bankDone) return;
      let n = 0;
      creep = setInterval(() => {
        decodeFrac += (0.95 - decodeFrac) * 0.1;
        emit();
        if (++n > 300) stopCreep();
      }, 150);
    },
  });
  const load = (file: string, kind: 'loop' | 'shot' = 'shot'): Promise<AudioBuffer | null> => {
    const key = `${kind}:${file}`;
    let p = inFlight.get(key);
    // ONE DECODE PER FILE. The mid ladder of a single-file car names the same bounce as its on and
    // off ladders, and used to be fetched and decoded again as a 'shot' (the 812: a second 38 MB of
    // float, a second declick pass, and a wasted try for a .ogg that does not exist). If the loop
    // path is already decoding that file, the answer is the same buffer.
    if (!p && kind === 'shot') p = inFlight.get(`loop:${file}`);
    if (!p) { p = loadOnce(file, kind); inFlight.set(key, p); }
    return p;
  };
  const loadOnce = async (file: string, kind: 'loop' | 'shot' = 'shot'): Promise<AudioBuffer | null> => {
    if (!base) return null; // synthetic car: nothing on disk, every listed sample falls back to procedural
    const hooks = kind === 'loop' ? bankHooks(file) : undefined;
    if (hasExtension(file)) return fetchDecode(ctx, `${base}/${file}${q}`, demoKey, hooks);
    // the order the formats are tried in is the one sampleFiles.ts publishes (the offline download asks
    // in the same order, so what it stores is what this asks for); once a kind has found its format,
    // that format goes first for the rest of the kind
    const order = chosen[kind]
      ? [chosen[kind]!, ...formats.filter((f) => f !== chosen[kind])]
      : candidateNames(file, kind, formats).map((n) => n.slice(file.length + 1));
    for (const ext of order) {
      const buf = await fetchDecode(ctx, `${base}/${file}.${ext}${q}`, demoKey, hooks);
      if (buf) {
        if (!chosen[kind]) {
          chosen[kind] = ext;
          report.format = chosen.loop && chosen.shot && chosen.loop !== chosen.shot ? `${chosen.loop}+${chosen.shot}` : (chosen.loop ?? chosen.shot);
        }
        return buf;
      }
    }
    return null;
  };
  let envReady: EnvironmentBuffers = { wind: null, debris: null, ambience: null, pitLife: [], music: null, story: null };
  const shots: OneShotSample[] = [
    ...(s.limiter ? [s.limiter] : []),
    ...(s.shiftUp ?? []),
    ...(s.shiftDown ?? []),
    ...(s.backfire ?? []),
    ...(s.wind ? [s.wind] : []),
    ...(s.debris ? [s.debris] : []),
    ...(s.pullaway ? [s.pullaway] : []),
    ...(s.settle ? [s.settle] : []),
    ...(s.idleBed ? [s.idleBed] : []),
    ...(s.starter ? [s.starter] : []),
    ...(s.startup ? [s.startup] : []),
    ...(s.shutoff ? [s.shutoff] : []),
    ...(s.falsestart ? [s.falsestart] : []),
    ...(s.falsestarts ?? []),
    ...(s.ambience ? [s.ambience] : []),
    ...(s.pitLife ?? []),
    ...(s.showroom?.music ? [s.showroom.music] : []),
    ...(s.showroom?.story ? [s.showroom.story] : []),
    ...(s.doorOpen ? [s.doorOpen] : []),
    ...(s.doorShut ? [s.doorShut] : []),
  ];
  total = s.on.length + s.off.length + shots.length;
  const tick = () => { done++; emit(); };
  emit();

  // ---- sweeps (long real passages + rpm curves) ----
  const sweeps: LoadedSweep[] = [];
  if (s.sweeps && base) {
    try {
      const res = await fetch(`${base}/sweeps.json${q}`, { cache: 'force-cache' });
      if (res.ok) {
        const j = (await res.json()) as { lowLvlRef?: { start: number; step: number; db: number[] }; sweeps: { id: string; file: string; mode: 'on' | 'off' | 'idle'; hop: number; rpm: number[]; thr?: number; lug?: boolean; low?: boolean; lvl?: number[] | null; cconf?: number[] | null }[] };
        await Promise.all(j.sweeps.map(async (sw) => {
          const buffer = await load(sw.file, 'loop');
          if (buffer) sweeps.push({ id: sw.id, mode: sw.mode, hop: sw.hop, rpm: sw.rpm, thr: sw.thr, lug: sw.lug, low: sw.low, lvl: sw.lvl, cconf: sw.cconf, buffer });
          else report.missing.push(sw.file);
        }));
        report.loopsReal += sweeps.length;
        (report as { lowLvlRef?: unknown }).lowLvlRef = j.lowLvlRef ?? null;
      } else report.missing.push('sweeps.json');
    } catch {
      report.missing.push('sweeps.json');
    }
  }
  // with sweeps the loops are not needed (they stay in the profile as the fallback for browsers without AudioWorklet)
  const wantLoops = !(sweeps.length && typeof AudioWorkletNode !== 'undefined');

  // Real files: fetch in parallel (cheap); decode failures → null.
  const decodedLoop = new Map<string, AudioBuffer | null>();
  const uniqueFiles = new Set<string>(wantLoops ? [...s.on, ...s.off].map((l) => l.file) : []);
  // decode the first file alone so the container choice is settled, then the rest in parallel
  const files = [...uniqueFiles];
  if (files.length) decodedLoop.set(files[0], await load(files[0], 'loop'));
  await Promise.all(files.slice(1).map(async (file) => decodedLoop.set(file, await load(file, 'loop'))));
  // the engine's voice is here and decoded: the bar's first 85 % are done, whatever it measured
  bankDone = true;
  stopCreep();
  emit();

  const buildSet = async (list: EngineLoopSample[], variant: 'on' | 'off'): Promise<LoadedLoop[]> => {
    const out: LoadedLoop[] = [];
    for (const l of list) {
      let buffer = decodedLoop.get(l.file) ?? null;
      let procedural = false;
      if (!buffer) {
        buffer = synthesizeEngineLoop(ctx, l.recordedRpm, profile.audio.procedural, variant);
        procedural = true;
        report.loopsProcedural++;
        report.missing.push(l.file);
        await yieldToUI(); // synthesis is CPU-bound; keep the UI alive
      } else {
        report.loopsReal++;
      }
      if (!procedural) declickBuffer(buffer, ladderEdges(profile));
      const fit = procedural ? { loopStart: undefined, loopEnd: undefined } : fitLoop(buffer, l.loopStart, l.loopEnd, l.recordedRpm, profile.audio.procedural.cylinders);
      out.push({ id: l.id, recordedRpm: l.recordedRpm, buffer, procedural, loopStart: fit.loopStart, loopEnd: fit.loopEnd });
      tick();
    }
    return out;
  };

  const on = wantLoops ? await buildSet(s.on, 'on') : [];
  const off = wantLoops ? await buildSet(s.off, 'off') : [];
  // A CAR WITH RECORDINGS MUST NEVER PLAY THE SYNTHESISER. The procedural voice exists for cars that
  // ship no samples at all; when a car HAS them and not one arrives, something is wrong upstream (the
  // entitlement was refused, the network died, the files 404) and quietly substituting an oscillator
  // bank is the worst possible answer: it sounds like a horn and it looks like the engine is broken.
  // Marc chased that noise for two days across the recordings, the mixer and the whole effects chain,
  // and it was this: /api/grant answering 'demo used up today', every file coming back 403, and the
  // fallback singing away. Say it instead.
  if (base && wantLoops && report.loopsReal === 0 && (s.on.length || s.off.length)) {
    report.denied = true;
  }

  // ALL AT ONCE, NOT ONE AFTER THE OTHER. These sixty files used to be awaited in a row: sixty round
  // trips end to end, which on a car's LTE is five seconds of waiting for nothing but latency (Marc:
  // "el audio tarda entre 5 y 7 segundos"). They are independent of each other, so they travel together.
  const loadShots = async (list: OneShotSample[] | undefined, fallback: (i: number) => AudioBuffer | null) => {
    const got = await Promise.all((list ?? []).map(async (sh, i) => {
      const b = await load(sh.file);
      tick();
      if (b) { report.oneShotsReal++; return b; }
      report.missing.push(sh.file);
      const f = fallback(i);
      if (f) { report.oneShotsProcedural++; return f; }
      return null;
    }));
    return got.filter((b): b is AudioBuffer => !!b);
  };

  // …and the twenty groups travel together too
  const [
    limiterArr, limiterCabArr, limiterXArr, launchArr, launchCabArr, launchXArr, lowLoopArr, shiftUp, shiftDown, backfire, pullawayArr, settleArr, idleBedArr,
    ovOnArr, ovOffArr, simOnArr, simOffArr, xOnArr, xOffArr, xMidArr, midArr, ovMidArr,
    tipArr, liftArr, tipCabArr, liftCabArr, tipXArr, liftXArr, turboArr, starterArr, startupArr, shutoffArr, falsestartArr, falsestarts,
    doorOpenArr, doorShutArr,
  ] = await Promise.all([
    loadShots(s.limiter ? [s.limiter] : [], () => null),
    loadShots(s.limiterCabin ? [s.limiterCabin] : [], () => null),
    loadShots(s.limiterExtra ? [s.limiterExtra] : [], () => null),
    loadShots(s.launch ? [s.launch] : [], () => null),
    loadShots(s.launchCabin ? [s.launchCabin] : [], () => null),
    loadShots(s.launchExtra ? [s.launchExtra] : [], () => null),
    loadShots(s.lowLoops, () => null),
    loadShots(s.shiftUp, () => synthesizeShift(ctx, 1)),
    loadShots(s.shiftDown, () => synthesizeShift(ctx, -1)),
    loadShots(s.backfire, (i) => synthesizeBackfire(ctx, i + 1)),
    loadShots(s.pullaway ? [s.pullaway] : [], () => null),
    loadShots(s.settle ? [s.settle] : [], () => null),
    loadShots(s.idleBed ? [s.idleBed] : [], () => null),
    loadShots(s.overlay?.on ?? [], () => null),
    loadShots(s.overlay?.off ?? [], () => null),
    loadShots(s.sim?.on ?? [], () => null),
    loadShots(s.sim?.off ?? [], () => null),
    loadShots(s.extra?.on ?? [], () => null),
    loadShots(s.extra?.off ?? [], () => null),
    loadShots(s.extra?.mid ?? [], () => null),
    loadShots(s.mid ?? [], () => null),
    loadShots(s.overlay?.mid ?? [], () => null),
    loadShots(s.tipIn ?? [], () => null),
    loadShots(s.liftOff ?? [], () => null),
    loadShots(s.overlay?.tipIn ?? [], () => null),
    loadShots(s.overlay?.liftOff ?? [], () => null),
    loadShots(s.extra?.tipIn ?? [], () => null),
    loadShots(s.extra?.liftOff ?? [], () => null),
    loadShots(s.turbo ? [s.turbo] : [], () => null),
    loadShots(s.starter ? [s.starter] : [], () => null),
    loadShots(s.startup ? [s.startup] : [], () => null),
    loadShots(s.shutoff ? [s.shutoff] : [], () => null),
    loadShots(s.falsestart ? [s.falsestart] : [], () => null),
    loadShots(s.falsestarts, () => null),
    loadShots(s.doorOpen ? [s.doorOpen] : [], () => null),
    loadShots(s.doorShut ? [s.doorShut] : [], () => null),
  ]);
  const lowLoops = lowLoopArr.map((buffer, i) => ({ buffer, rpm: s.lowLoops?.[i]?.recordedRpm ?? 2000 }));
  const mkOv = (arr: (AudioBuffer | null)[], defs: EngineLoopSample[] = []): LoadedLoop[] => arr.map((buffer, i) => {
    if (buffer) declickBuffer(buffer, ladderEdges(profile));
    const rpm = defs[i]?.recordedRpm ?? 2000;
    const fit = buffer ? fitLoop(buffer, defs[i]?.loopStart, defs[i]?.loopEnd, rpm, profile.audio.procedural.cylinders) : { loopStart: defs[i]?.loopStart, loopEnd: defs[i]?.loopEnd };
    return { id: defs[i]?.id ?? `ov_${i}`, recordedRpm: rpm, buffer: buffer as AudioBuffer, procedural: false, loopStart: fit.loopStart, loopEnd: fit.loopEnd };
  }).filter((x) => !!x.buffer);

  // THE PIT BOX'S ATMOSPHERE COMES SECOND. Wind, stones, the garage tone and the cars going past are
  // 3 of the 10 MB, and none of them is the engine: they only speak with the car parked and the engine
  // off. So the engine starts on what it needs, and this arrives behind it (Marc: 5-7 s before a sound).
  const envLater = (async () => {
    const [windArr, debrisArr, ambienceArr, pitLife, musicArr, storyArr] = await Promise.all([
      loadShots(s.wind ? [s.wind] : [], () => null),
      loadShots(s.debris ? [s.debris] : [], () => null),
      loadShots(s.ambience ? [s.ambience] : [], () => null),
      loadShots(s.pitLife, () => null),
      loadShots(s.showroom?.music ? [s.showroom.music] : [], () => null),
      loadShots(s.showroom?.story ? [s.showroom.story] : [], () => null),
    ]);
    return { wind: windArr[0] ?? null, debris: debrisArr[0] ?? null, ambience: ambienceArr[0] ?? null, pitLife, music: musicArr[0] ?? null, story: storyArr[0] ?? null };
  })();
  if (onEnvironment) void envLater.then(onEnvironment);
  else { const e = await envLater; envReady = e; }

  report.missing = [...new Set(report.missing)];
  return {
    on,
    off,
    sweeps,
    oneShots: { limiter: limiterArr[0] ?? null, limiterCabin: limiterCabArr[0] ?? null, limiterExtra: limiterXArr[0] ?? null, limiterRpm: s.limiter?.recordedRpm ?? 0, launch: launchArr[0] ?? null, launchCabin: launchCabArr[0] ?? null, launchExtra: launchXArr[0] ?? null, launchRpm: s.launch?.recordedRpm ?? 0, lowLoops, wind: envReady.wind, debris: envReady.debris, pullaway: pullawayArr[0] ?? null, settle: settleArr[0] ?? null, idleBed: idleBedArr[0] ?? null, overlayOn: mkOv(ovOnArr, s.overlay?.on), overlayOff: mkOv(ovOffArr, s.overlay?.off), simOn: mkOv(simOnArr, s.sim?.on), simOff: mkOv(simOffArr, s.sim?.off), extraOn: mkOv(xOnArr, s.extra?.on), extraOff: mkOv(xOffArr, s.extra?.off), extraMid: mkOv(xMidArr, s.extra?.mid), midLoops: mkOv(midArr, s.mid), overlayMid: mkOv(ovMidArr, s.overlay?.mid), tipIn: mkOv(tipArr, s.tipIn), liftOff: mkOv(liftArr, s.liftOff), tipInCabin: mkOv(tipCabArr, s.overlay?.tipIn), liftOffCabin: mkOv(liftCabArr, s.overlay?.liftOff), tipInExtra: mkOv(tipXArr, s.extra?.tipIn), liftOffExtra: mkOv(liftXArr, s.extra?.liftOff), turbo: turboArr[0] ?? null, starter: starterArr[0] ?? null, startup: startupArr[0] ?? null, shutoff: shutoffArr[0] ?? null, falsestart: falsestartArr[0] ?? null, falsestarts, ambience: envReady.ambience, pitLife: envReady.pitLife, music: envReady.music, story: envReady.story, doorOpen: doorOpenArr[0] ?? null, doorShut: doorShutArr[0] ?? null, shiftUp, shiftDown, backfire, shiftUpRpm: (s.shiftUp ?? []).map((x) => x.recordedRpm ?? 0), shiftDownRpm: (s.shiftDown ?? []).map((x) => x.recordedRpm ?? 0) },
    report,
  };
}
